import React, { useState } from "react";
import { toast } from "react-toastify";
import * as hrmsService from "../../../services/hrmsService";
import { FormDrawer } from "../hrForm";
import { EmptyState, LoadState, Tabs } from "../hrComponents";
import { cardStyle, formatDate, iconBtnStyle, statusBadge, statusLabel, tdStyle, thStyle, todayYmd, useLoad, addDaysYmd } from "../hrUi";

// ABS-01..04: unplanned absences waiting to be sorted out, alerts for possible absconding, and the pattern of absence.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };

function Absences({ isAdmin }) {
  const [status, setStatus] = useState("OPEN");
  const { data, loading, error, reload } = useLoad(() => hrmsService.listAbsences({ status }), [status]);
  const [excusing, setExcusing] = useState(null);
  const rows = data || [];
  return (
    <>
      <div className="d-flex gap-2 mb-3">
        {[["OPEN", "Open"], ["LOP", "Loss of pay"], ["EXCUSED", "Excused"]].map(([v, label]) => (
          <button key={v} type="button" className={`btn btn-sm ${status === v ? "btn-danger" : "btn-outline-secondary"}`} onClick={() => setStatus(v)}>
            {label}
          </button>
        ))}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title={status === "OPEN" ? "No open absences" : "Nothing here"} text={status === "OPEN" ? "A day with no punch and no leave appears here the next morning." : undefined} />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 520 }}>
              <thead>
                <tr>
                  {["Employee", "Day", "Status", "Note", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td style={tdStyle}>
                      {a.full_name} <span style={{ color: "#6B7280", fontSize: 12 }}>{a.employee_code}</span>
                    </td>
                    <td style={tdStyle}>{formatDate(a.absent_date)}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(a.status === "OPEN" ? "PENDING" : a.status === "LOP" ? "REJECTED" : "APPROVED")}>{statusLabel(a.status)}</span>
                    </td>
                    <td style={tdStyle}>{a.resolution_note || "—"}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {isAdmin && ["OPEN", "LOP"].includes(a.status) ? (
                        <button type="button" style={linkBtn} onClick={() => setExcusing(a)}>
                          Excuse
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={!!excusing}
        title="Excuse this absence"
        fields={[{ key: "note", label: "Why is it excused?", type: "textarea", required: true, max: 300, rows: 3 }]}
        onHide={() => setExcusing(null)}
        onSave={(clean) => hrmsService.excuseAbsence(excusing.id, clean.note)}
        onDone={reload}
        submitLabel="Excuse"
      />
    </>
  );
}

function Alerts() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listAbscondingAlerts("OPEN"), []);
  const rows = data || [];
  const close = async (a, status) => {
    const res = await hrmsService.closeAbscondingAlert(a.id, status).catch((e) => ({ success: false, message: e.message }));
    if (res?.success) reload();
    else toast.error(hrmsService.responseMessage(res));
  };
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {rows.length === 0 ? (
        <EmptyState title="No alerts" text="HR is alerted when someone is absent for several working days in a row." />
      ) : (
        <div style={{ ...cardStyle, padding: "4px 16px" }}>
          {rows.map((a) => (
            <div key={a.id} className="d-flex flex-wrap justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid #F3F4F6", fontSize: 14 }}>
              <span>
                <strong>{a.full_name}</strong> absent {a.days} working days in a row since {formatDate(a.first_absent_date)}
              </span>
              <span>
                <button type="button" style={linkBtn} onClick={() => close(a, "ACTIONED")}>
                  Action taken
                </button>{" "}
                <button type="button" style={linkBtn} onClick={() => close(a, "DISMISSED")}>
                  Dismiss
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </LoadState>
  );
}

function Analytics() {
  const to = todayYmd();
  const [range, setRange] = useState(90);
  const { data, loading, error, reload } = useLoad(() => hrmsService.getAbsenceAnalytics(addDaysYmd(to, -range), to), [range]);
  return (
    <>
      <div className="d-flex gap-2 mb-3">
        {[30, 90, 180].map((n) => (
          <button key={n} type="button" className={`btn btn-sm ${range === n ? "btn-danger" : "btn-outline-secondary"}`} onClick={() => setRange(n)}>
            Last {n} days
          </button>
        ))}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data ? (
          <>
            <div className="d-flex gap-3 mb-3">
              <div style={{ ...cardStyle, padding: "12px 18px" }}>
                <div style={{ fontSize: 12, color: "#6B7280" }}>Absence rate</div>
                <div style={{ fontSize: 26, fontWeight: 700 }}>{data.absence_rate}%</div>
              </div>
              <div style={{ ...cardStyle, padding: "12px 18px" }}>
                <div style={{ fontSize: 12, color: "#6B7280" }}>Absent days</div>
                <div style={{ fontSize: 26, fontWeight: 700 }}>{data.total_absent_days}</div>
              </div>
            </div>
            {data.top.length === 0 ? (
              <EmptyState title="No absences in this period" />
            ) : (
              <div style={{ ...cardStyle, overflowX: "auto" }}>
                <table className="table mb-0" style={{ minWidth: 560 }}>
                  <thead>
                    <tr>
                      {["Employee", "Days absent", "Spells", "Bradford factor", "Rate", "Mon / Fri"].map((h) => (
                        <th key={h} style={thStyle}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.top.map((p) => (
                      <tr key={p.employee_id}>
                        <td style={tdStyle}>{p.full_name}</td>
                        <td style={tdStyle}>{p.absent_days}</td>
                        <td style={tdStyle}>{p.spells}</td>
                        <td style={{ ...tdStyle, fontWeight: 600, color: p.bradford >= 250 ? "#B91C1C" : p.bradford >= 50 ? "#B45309" : undefined }}>{p.bradford}</td>
                        <td style={tdStyle}>{p.absence_rate}%</td>
                        <td style={tdStyle}>{Math.round(p.monday_friday_share * 100)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 8 }}>The Bradford factor is spells squared times days absent. Many short absences score far higher than one long one.</div>
          </>
        ) : null}
      </LoadState>
    </>
  );
}

export default function AbsenceTab({ isAdmin }) {
  const [tab, setTab] = useState("open");
  return (
    <>
      <Tabs tabs={[{ key: "open", label: "Absences" }, ...(isAdmin ? [{ key: "alerts", label: "Absconding alerts" }] : []), { key: "analytics", label: "Analytics" }]} active={tab} onChange={setTab} />
      {tab === "open" ? <Absences isAdmin={isAdmin} /> : null}
      {tab === "alerts" ? <Alerts /> : null}
      {tab === "analytics" ? <Analytics /> : null}
    </>
  );
}
